import { useState, useEffect, useCallback } from 'react';
import organizationService from '../../services/organization';
import type { Organization } from '../../services/organization';
import Button from '../ui/Button';
import Input from '../ui/Input';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import { ORGANISATION_TYPES } from '../../constants/organisationTypes';

// Canonical list lives in src/constants/organisationTypes.ts, mirrored from
// the server. Licensor and the licensee roles are deliberately absent: they
// describe a relationship, and are read from the licence records.

// Common timezones grouped by region
const TIMEZONES = [
  { group: 'Australia', zones: [
    { value: 'Australia/Melbourne', label: 'Melbourne (AEST/AEDT)' },
    { value: 'Australia/Sydney', label: 'Sydney (AEST/AEDT)' },
    { value: 'Australia/Brisbane', label: 'Brisbane (AEST)' },
    { value: 'Australia/Adelaide', label: 'Adelaide (ACST/ACDT)' },
    { value: 'Australia/Perth', label: 'Perth (AWST)' },
    { value: 'Australia/Darwin', label: 'Darwin (ACST)' },
    { value: 'Australia/Hobart', label: 'Hobart (AEST/AEDT)' },
  ]},
  { group: 'New Zealand', zones: [
    { value: 'Pacific/Auckland', label: 'Auckland (NZST/NZDT)' },
    { value: 'Pacific/Chatham', label: 'Chatham Islands' },
  ]},
  { group: 'Asia', zones: [
    { value: 'Asia/Singapore', label: 'Singapore (SGT)' },
    { value: 'Asia/Hong_Kong', label: 'Hong Kong (HKT)' },
    { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
    { value: 'Asia/Shanghai', label: 'Shanghai (CST)' },
    { value: 'Asia/Kolkata', label: 'India (IST)' },
    { value: 'Asia/Dubai', label: 'Dubai (GST)' },
  ]},
  { group: 'Europe', zones: [
    { value: 'Europe/London', label: 'London (GMT/BST)' },
    { value: 'Europe/Paris', label: 'Paris (CET/CEST)' },
    { value: 'Europe/Berlin', label: 'Berlin (CET/CEST)' },
    { value: 'Europe/Amsterdam', label: 'Amsterdam (CET/CEST)' },
  ]},
  { group: 'Americas', zones: [
    { value: 'America/New_York', label: 'New York (EST/EDT)' },
    { value: 'America/Chicago', label: 'Chicago (CST/CDT)' },
    { value: 'America/Denver', label: 'Denver (MST/MDT)' },
    { value: 'America/Los_Angeles', label: 'Los Angeles (PST/PDT)' },
    { value: 'America/Toronto', label: 'Toronto (EST/EDT)' },
    { value: 'America/Vancouver', label: 'Vancouver (PST/PDT)' },
  ]},
  { group: 'UTC', zones: [
    { value: 'UTC', label: 'UTC (Coordinated Universal Time)' },
  ]},
];

interface DetailsForm {
  name: string;
  abn: string;
  contact_email: string;
  contact_phone: string;
  website: string;
  address_line1: string;
  address_line2: string;
  city: string;
  state: string;
  postcode: string;
  country: string;
}

const EMPTY_DETAILS: DetailsForm = {
  name: '', abn: '', contact_email: '', contact_phone: '', website: '',
  address_line1: '', address_line2: '', city: '', state: '', postcode: '', country: '',
};

function OrganisationSettings() {
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [selectedTimezone, setSelectedTimezone] = useState<string>('Australia/Melbourne');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [details, setDetails] = useState<DetailsForm>(EMPTY_DETAILS);
  const [savedDetails, setSavedDetails] = useState<DetailsForm>(EMPTY_DETAILS);
  const [detailsSaving, setDetailsSaving] = useState(false);
  const [detailsError, setDetailsError] = useState<string | null>(null);
  const [detailsSuccess, setDetailsSuccess] = useState<string | null>(null);

  const detailsDirty = JSON.stringify(details) !== JSON.stringify(savedDetails);

  const initDetails = useCallback((org: Organization) => {
    const d: DetailsForm = {
      name: org.name || '',
      abn: org.abn || '',
      contact_email: org.contact_email || '',
      contact_phone: org.contact_phone || '',
      website: org.website || '',
      address_line1: org.address_line1 || '',
      address_line2: org.address_line2 || '',
      city: org.city || '',
      state: org.state || '',
      postcode: org.postcode || '',
      country: org.country || '',
    };
    setDetails(d);
    setSavedDetails(d);
  }, []);

  // Organisation types state
  const [activeTypes, setActiveTypes] = useState<string[]>([]);
  const [typesLoading, setTypesLoading] = useState(false);
  const [typesError, setTypesError] = useState<string | null>(null);
  const [typesSuccess, setTypesSuccess] = useState<string | null>(null);

  useEffect(() => {
    fetchOrganization();
    fetchOrgTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchOrgTypes = async () => {
    setTypesLoading(true);
    try {
      const response = await organizationService.getOrgTypes();
      if (response.success && response.data) {
        setActiveTypes(response.data.map((t: { stakeholder_type: string }) => t.stakeholder_type));
      }
    } catch {
      setActiveTypes([]);
    } finally {
      setTypesLoading(false);
    }
  };

  const handleToggleType = async (type: string) => {
    setTypesError(null);
    setTypesSuccess(null);
    const isActive = activeTypes.includes(type);
    try {
      if (isActive) {
        await organizationService.removeOrgType(type);
        setActiveTypes(prev => prev.filter(t => t !== type));
      } else {
        await organizationService.addOrgType(type);
        setActiveTypes(prev => [...prev, type]);
      }
      setTypesSuccess('Organisation types updated');
      setTimeout(() => setTypesSuccess(null), 3000);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to update type';
      setTypesError(msg);
    }
  };

  const fetchOrganization = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await organizationService.getCurrentOrganization();
      if (response.success && response.data) {
        setOrganization(response.data);
        setSelectedTimezone(response.data.timezone || 'Australia/Melbourne');
        initDetails(response.data);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load organisation settings');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDetailChange = (field: keyof DetailsForm) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setDetails(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handleSaveDetails = async () => {
    if (!organization) return;
    try {
      setDetailsSaving(true);
      setDetailsError(null);
      setDetailsSuccess(null);

      const response = await organizationService.update(organization.id, {
        name: details.name,
        abn: details.abn || null,
        contact_email: details.contact_email || null,
        contact_phone: details.contact_phone || null,
        website: details.website || null,
        address_line1: details.address_line1 || null,
        address_line2: details.address_line2 || null,
        city: details.city || null,
        state: details.state || null,
        postcode: details.postcode || null,
        country: details.country || null,
      });

      if (response.success) {
        setDetailsSuccess('Organisation details updated');
        // Backend wraps response as { organisation: {...} }
        const org = (response.data as any)?.organisation || response.data;
        if (org) {
          setOrganization(org);
          initDetails(org);
        } else {
          setSavedDetails({ ...details });
        }
        setTimeout(() => setDetailsSuccess(null), 3000);
      } else {
        throw new Error('Failed to update details');
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to update details';
      setDetailsError(msg);
    } finally {
      setDetailsSaving(false);
    }
  };

  const handleSave = async () => {
    if (!organization) return;

    try {
      setIsSaving(true);
      setError(null);
      setSuccess(null);

      const response = await organizationService.update(organization.id, {
        timezone: selectedTimezone,
      });

      if (response.success) {
        setSuccess('Timezone updated successfully');
        setOrganization(response.data);
      } else {
        throw new Error('Failed to update timezone');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to update timezone');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <svg className="h-5 w-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            Organisation Settings
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <svg className="animate-spin h-8 w-8 text-blue-600" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <svg className="h-5 w-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          Organisation Settings
        </CardTitle>
      </CardHeader>
      <CardContent>
        {error && (
          <div className="mb-4 rounded-lg bg-red-50 border border-red-200 p-3">
            <div className="flex items-center">
              <svg className="h-4 w-4 text-red-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        )}

        {success && (
          <div className="mb-4 rounded-lg bg-green-50 border border-green-200 p-3">
            <div className="flex items-center">
              <svg className="h-4 w-4 text-green-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <p className="text-sm text-green-800">{success}</p>
            </div>
          </div>
        )}

        <div className="space-y-4">
          {/* Organisation Details */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-900">Organisation Details</h3>

            {detailsError && (
              <div className="rounded-lg bg-red-50 border border-red-200 p-3">
                <p className="text-sm text-red-800">{detailsError}</p>
              </div>
            )}
            {detailsSuccess && (
              <div className="rounded-lg bg-green-50 border border-green-200 p-3">
                <p className="text-sm text-green-800">{detailsSuccess}</p>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input label="Organisation Name" value={details.name} onChange={handleDetailChange('name')} required />
              <Input label="ABN/ACN" value={details.abn} onChange={handleDetailChange('abn')} placeholder="e.g. 12 345 678 901 or ACN 123 456 789" />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input label="Contact Email" type="email" value={details.contact_email} onChange={handleDetailChange('contact_email')} />
              <Input label="Contact Phone" type="tel" value={details.contact_phone} onChange={handleDetailChange('contact_phone')} />
            </div>

            <Input label="Website" type="url" value={details.website} onChange={handleDetailChange('website')} placeholder="https://" />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input label="Address Line 1" value={details.address_line1} onChange={handleDetailChange('address_line1')} />
              <Input label="Address Line 2" value={details.address_line2} onChange={handleDetailChange('address_line2')} />
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <Input label="City" value={details.city} onChange={handleDetailChange('city')} />
              <Input label="State" value={details.state} onChange={handleDetailChange('state')} />
              <Input label="Postcode" value={details.postcode} onChange={handleDetailChange('postcode')} />
              <Input label="Country" value={details.country} onChange={handleDetailChange('country')} />
            </div>

            <div className="flex justify-end">
              <Button onClick={handleSaveDetails} disabled={detailsSaving || !detailsDirty || !details.name.trim()}>
                {detailsSaving ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Saving...
                  </span>
                ) : 'Save Details'}
              </Button>
            </div>
          </div>

          <div className="border-t border-gray-200 pt-4">
            <label htmlFor="timezone" className="block text-sm font-medium text-gray-700 mb-1">
              Timezone
            </label>
            <p className="text-xs text-gray-500 mb-2">
              This timezone is used for timesheet date calculations. All clock-in/out times will be recorded using this timezone.
            </p>
            <select
              id="timezone"
              value={selectedTimezone}
              onChange={(e) => setSelectedTimezone(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {TIMEZONES.map((group) => (
                <optgroup key={group.group} label={group.group}>
                  {group.zones.map((zone) => (
                    <option key={zone.value} value={zone.value}>
                      {zone.label}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>

          <div className="flex justify-end pt-4">
            <Button
              onClick={handleSave}
              disabled={isSaving || selectedTimezone === organization?.timezone}
            >
              {isSaving ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Saving...
                </span>
              ) : (
                'Save Changes'
              )}
            </Button>
          </div>

          {/* Organisation Types — always visible */}
          <div className="border-t border-gray-200 pt-6 mt-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Organisation Type
            </label>
            <p className="text-xs text-gray-500 mb-3">
              Select the type(s) that describe this organisation (e.g. nursery, orchard, packhouse). Used across the platform to tailor features and filter organisations.
            </p>

            {typesError && (
              <div className="mb-3 rounded-lg bg-red-50 border border-red-200 p-2 text-sm text-red-800">{typesError}</div>
            )}
            {typesSuccess && (
              <div className="mb-3 rounded-lg bg-green-50 border border-green-200 p-2 text-sm text-green-800">{typesSuccess}</div>
            )}

            {typesLoading ? (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Loading...
              </div>
            ) : (
              <div className="space-y-2">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {ORGANISATION_TYPES.map((type) => {
                    const active = activeTypes.includes(type.value);
                    return (
                      <button
                        key={type.value}
                        type="button"
                        aria-pressed={active}
                        onClick={() => handleToggleType(type.value)}
                        className={`text-left px-3 py-2.5 rounded-lg border transition-colors ${
                          active
                            ? 'bg-blue-50 border-blue-300'
                            : 'bg-white border-gray-200 hover:border-blue-300'
                        }`}
                      >
                        <span className={`block text-sm font-medium ${active ? 'text-blue-900' : 'text-gray-800'}`}>
                          {active && <span className="mr-1">✓</span>}
                          {type.label}
                        </span>
                        <span className="block text-xs text-gray-500">{type.description}</span>
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-gray-500">
                  These describe what your organisation is. Whether you hold a licence, or grant
                  one, is read from the licence records themselves — there is nothing to tick here
                  for that.
                </p>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default OrganisationSettings;
